import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { useAsync, Banner, Empty } from './ui';
import { FIELD_META } from '../../lib/readingColumns';
import { isPmsTitle, pmsGroupLabel } from '../../lib/pmsGroup';

const LEAD = new Set([
  'Submission ID', 'Server Timestamp', 'Client Captured At', 'Effective Reading At', 'Source',
  'Date', 'Time', 'NFC Tag Code', 'Equipment ID', 'Equipment Name', 'Location', 'Operator ID', 'Operator Name',
  'Observation / Remarks', 'Abnormal Status', 'Abnormal Fields', 'Operator Confirmed Abnormal',
  'Sync Status', 'Reject Reason', 'App Version', 'Approved By', 'Approved By Name', 'Approved At',
]);
const daysAgoISO = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

/** Full chronological reading history, browse-only — approving/PDFs for PMS live under "PMS reports". */
export default function ReadingsAdmin() {
  const [sp] = useSearchParams();
  const initial = {
    from: sp.get('from') || daysAgoISO(7),
    to: sp.get('to') || daysAgoISO(0),
    equipmentId: sp.get('equipmentId') || '',
    operatorId: sp.get('operatorId') || '',
    status: sp.get('status') || '',
    templateId: sp.get('templateId') || '',
  };
  const [filter, setFilter] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [catFilter, setCatFilter] = useState<'all' | 'daily' | 'pms'>('all');
  const templates = useAsync(() => adminApi.listTemplates(), [], 'templates');
  const equipment = useAsync(() => adminApi.listEquipment(), [], 'equipment');
  const { data, error, loading } = useAsync(
    () => adminApi.listReadings(clean(applied), 300),
    [applied],
    `readings:${JSON.stringify(clean(applied))}`,
  );

  const equipById = useMemo(() => {
    const m = new Map<string, { category: string }>();
    equipment.data?.rows.forEach((r) => m.set(String(r['Equipment ID']), {
      category: String(r['Category'] ?? 'daily'),
    }));
    return m;
  }, [equipment.data]);

  const dailyTemplates = useMemo(() => (templates.data?.templates ?? []).filter((t) => !isPmsTitle(t.title)), [templates.data]);
  const pmsTemplateGroups = useMemo(() => {
    const pms = (templates.data?.templates ?? []).filter((t) => isPmsTitle(t.title));
    const order: string[] = [];
    const byLabel = new Map<string, typeof pms>();
    pms.forEach((t) => {
      const label = pmsGroupLabel(t.title);
      if (!byLabel.has(label)) { byLabel.set(label, []); order.push(label); }
      byLabel.get(label)!.push(t);
    });
    return order.map((label) => ({ label, items: byLabel.get(label)! }));
  }, [templates.data]);

  function clean(f: typeof filter) {
    const o: Record<string, string> = {};
    Object.entries(f).forEach(([k, v]) => { if (v) o[k] = v; });
    return o;
  }

  const allRows = data?.rows ?? [];
  const rows = useMemo(
    () => (catFilter === 'all' ? allRows : allRows.filter((r) => (equipById.get(String(r['Equipment ID']))?.category ?? 'daily') === catFilter)),
    [allRows, catFilter, equipById],
  );
  const dailyCount = allRows.filter((r) => (equipById.get(String(r['Equipment ID']))?.category ?? 'daily') === 'daily').length;
  const pmsCount = allRows.filter((r) => equipById.get(String(r['Equipment ID']))?.category === 'pms').length;
  const metricCols = useMemo(() => {
    if (!rows.length) return [] as string[];
    return Object.keys(rows[0]).filter((k) => !LEAD.has(k));
  }, [rows]);

  return (
    <div>
      <h1>Readings history</h1>
      <p className="muted" style={{ marginTop: -6 }}>
        Full chronological log, read-only. Approving a PMS check and generating its report PDF live under <strong>PMS reports</strong>.
      </p>
      <div className="choice-group" role="tablist" aria-label="Reading type" style={{ marginBottom: 12 }}>
        <button type="button" className={`choice-btn ${catFilter === 'all' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'all'} onClick={() => setCatFilter('all')}>
          All ({allRows.length})
        </button>
        <button type="button" className={`choice-btn ${catFilter === 'daily' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'daily'} onClick={() => setCatFilter('daily')}>
          Daily readings ({dailyCount})
        </button>
        <button type="button" className={`choice-btn ${catFilter === 'pms' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'pms'} onClick={() => setCatFilter('pms')}>
          PMS checks ({pmsCount})
        </button>
      </div>
      <div className="filters">
        <div><label>From</label><input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} /></div>
        <div><label>To</label><input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} /></div>
        <div><label>Report</label>
          <select value={filter.templateId} onChange={(e) => setFilter({ ...filter, templateId: e.target.value })}>
            <option value="">all reports</option>
            {catFilter !== 'pms' && dailyTemplates.map((t) => (
              <option key={t.templateId} value={t.templateId}>{t.title}</option>
            ))}
            {catFilter !== 'daily' && pmsTemplateGroups.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.items.map((t) => (
                  <option key={t.templateId} value={t.templateId}>{t.title}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div><label>Equipment ID</label><input value={filter.equipmentId} onChange={(e) => setFilter({ ...filter, equipmentId: e.target.value.toUpperCase() })} /></div>
        <div><label>Operator ID</label><input value={filter.operatorId} onChange={(e) => setFilter({ ...filter, operatorId: e.target.value.toUpperCase() })} /></div>
        <div><label>Status</label>
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
            <option value="">any</option>
            <option value="abnormal">abnormal (warning + critical)</option>
            <option value="normal">normal</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </select>
        </div>
        <button className="sm" onClick={() => setApplied({ ...filter })}>Apply</button>
      </div>

      <Banner error={error} />
      {loading && <div className="spinner" />}
      {!loading && rows.length === 0 && <Empty icon="📖">No readings match.</Empty>}

      {rows.map((r, i) => {
        const st = String(r['Abnormal Status'] ?? 'normal').toLowerCase();
        const tone = st === 'critical' ? 'bad' : st === 'warning' ? 'warn' : 'ok';
        const submissionId = String(r['Submission ID']);
        const isPms = equipById.get(String(r['Equipment ID']))?.category === 'pms';
        const approvedByName = r['Approved By Name'] ? String(r['Approved By Name']) : '';
        return (
          <details className="rrow" key={i}>
            <summary>
              <span className={`chip ${tone}`}>{st}</span>
              <span>
                <span className="rtime">{String(r['Date'])} {String(r['Time'])}</span>
                <span className="rmeta"> · {String(r['Equipment Name'])} · {String(r['Operator Name'])}</span>
              </span>
              <span className="muted" style={{ fontSize: '.8rem' }}>{String(r['Source'] ?? '')}</span>
            </summary>
            <div className="rdetail">
              <div className="kv">
                {metricCols.map((c) => (
                  <div key={c}><span className="kk">{prettyCol(c)}</span><span>{String(r[c] ?? '—')}</span></div>
                ))}
              </div>
              {r['Observation / Remarks'] ? <p style={{ marginTop: 10 }}><span className="muted">Remarks: </span>{String(r['Observation / Remarks'])}</p> : null}
              {r['Abnormal Fields'] ? <p className="tiny" style={{ color: 'var(--warn)' }}>{String(r['Abnormal Fields'])}</p> : null}
              <p className="muted tiny">ID {submissionId.slice(0, 8)} · server {String(r['Server Timestamp'])}</p>

              {isPms && (
                <p className="tiny" style={{ marginTop: 10 }}>
                  {approvedByName
                    ? <span style={{ color: 'var(--ok)' }}>✓ Approved by {approvedByName}</span>
                    : <span className="muted">Not yet reviewed — see PMS reports</span>}
                </p>
              )}
            </div>
          </details>
        );
      })}
    </div>
  );
}

const COL_LABEL = Object.fromEntries(FIELD_META.map((f) => [f.col, f.label]));
function prettyCol(c: string) { return COL_LABEL[c] ?? c; }
